import { useMutation } from '@tanstack/react-query';
import { BellRing, Copy, Loader2, MessageSquare } from 'lucide-react';
import {
  LinkBase,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'ra-core';
import { useNavigate } from 'react-router-dom';
import { type AdminDataProvider } from '../../api/adminDataProvider';
import { IconButtonWithTooltip } from '../../components/admin/icon-button-with-tooltip';
import { CopyGrowAppLinkButton } from '../../components/custom/CopyGrowAppLinkButton';
import { RowActions } from '../../components/custom/RowActions';
import { buttonVariants } from '../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { GetNudgeReportButton } from './GetNudgeReportButton';

/** The row actions of a nudge: remind, duplicate, feedback, report and copy link */
export const NudgeActions = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const translate = useTranslate();
  const id = record?.id;

  const remind = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Record context is not available');
      return dataProvider.remindNudge(id);
    },
    onSuccess: () =>
      notify('moonstar.nudges.remind.success', { type: 'success' }),
    onError: (error) =>
      notify('moonstar.nudges.remind.error', {
        type: 'error',
        messageArgs: { message: error.message },
      }),
  });

  const duplicate = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Record context is not available');
      return dataProvider.duplicateNudge(id);
    },
    onSuccess: async (data) => {
      await navigate(`/nudges/${data.data.id}`);
      refresh();
    },
    onError: (error) =>
      notify('moonstar.nudges.duplicate.error', {
        type: 'error',
        messageArgs: { message: error.message },
      }),
  });

  const feedbackLabel = translate('resources.nudges.fields.nudgeFeedbacks');

  return (
    <RowActions className="items-center gap-0.5 text-primary">
      <IconButtonWithTooltip
        label="moonstar.nudges.actions.sendReminder"
        onClick={() => remind.mutate()}
        disabled={remind.isPending}
      >
        <BellRing />
      </IconButtonWithTooltip>
      <IconButtonWithTooltip
        label="moonstar.nudges.actions.duplicate"
        onClick={() => duplicate.mutate()}
        disabled={duplicate.isPending}
      >
        {duplicate.isPending ? <Loader2 className="animate-spin" /> : <Copy />}
      </IconButtonWithTooltip>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            render={
              <LinkBase
                to={`/nudges/${record?.id}/feedbacks`}
                aria-label={feedbackLabel}
                className={buttonVariants({ variant: 'ghost', size: 'icon' })}
              />
            }
          >
            <MessageSquare />
          </TooltipTrigger>
          <TooltipContent>{feedbackLabel}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <GetNudgeReportButton />
      <CopyGrowAppLinkButton path={`/nudges/${record?.id}`} iconOnly />
    </RowActions>
  );
};
